import React, { useEffect, useState, useCallback, memo } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Box,
  useTheme,
  useMediaQuery,
  Typography
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  LocalShipping as LocalShippingIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  LocationOn as LocationOnIcon,
  Notifications as NotificationsIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSystemSettings } from '../../contexts/SystemSettingsContext';
import { UserRole } from '../../types/user';

interface SidebarProps {
  mobileOpen?: boolean;
  onDrawerToggle?: () => void;
  onLogout?: () => void;
  open?: boolean;
  onClose?: () => void;
  onToggle?: () => void;
}

const menuItems = [
  {
    title: 'ダッシュボード',
    path: '/dashboard',
    icon: <DashboardIcon />,
    roles: [UserRole.ADMIN, UserRole.MANAGER, UserRole.DRIVER, UserRole.CAST, UserRole.CUSTOMER]
  },
  {
    title: 'ユーザー管理',
    path: '/users',
    icon: <PeopleIcon />,
    roles: [UserRole.ADMIN]
  },
  {
    title: '顧客管理',
    path: '/customers',
    icon: <PeopleIcon />,
    roles: [UserRole.ADMIN]
  },
  {
    title: 'オーダー管理',
    path: '/orders',
    icon: <LocalShippingIcon />,
    roles: [UserRole.ADMIN, UserRole.MANAGER, UserRole.DRIVER, UserRole.CAST]
  },
  {
    title: 'シフト管理',
    path: '/shifts',
    icon: <ScheduleIcon />,
    roles: [UserRole.ADMIN, UserRole.MANAGER, UserRole.DRIVER, UserRole.CAST]
  },
  {
    title: 'GPS追跡',
    path: '/gps-tracking',
    icon: <LocationOnIcon />,
    roles: [UserRole.ADMIN, UserRole.MANAGER, UserRole.DRIVER]
  },
  {
    title: 'レポート',
    path: '/reports',
    icon: <AssessmentIcon />,
    roles: [UserRole.ADMIN, UserRole.MANAGER]
  },
  {
    title: '通知設定',
    path: '/notification-settings',
    icon: <NotificationsIcon />,
    roles: [UserRole.ADMIN, UserRole.MANAGER, UserRole.DRIVER, UserRole.CAST]
  },
  {
    title: '設定',
    path: '/settings',
    icon: <SettingsIcon />,
    roles: [UserRole.ADMIN]
  }
];

const Sidebar: React.FC<SidebarProps> = memo(({ 
  mobileOpen: propMobileOpen,
  onDrawerToggle: propOnDrawerToggle,
  onLogout: propOnLogout,
  open: propOpen,
  onClose: propOnClose,
  onToggle: propOnToggle
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [logoLoaded, setLogoLoaded] = useState(true);
  const { systemSettings } = useSystemSettings();

  const storeName = systemSettings?.store_name || 
                   'ディスパッチマネージャー';

  const mobileOpen = propMobileOpen ?? propOpen ?? false;
  const onDrawerToggle = propOnDrawerToggle ?? propOnToggle ?? (() => {});
  const onLogout = propOnLogout ?? (() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    navigate('/login');
  });

  const drawerWidth = isMobile ? 280 : 280;

  // メニュー項目をフィルタリングしてレンダリングする関数をメモ化
  const filteredMenuItems = useCallback(() => {
    if (!user?.role) return [];
    // user.role は 'CAST' のように大文字で格納されるが、UserRole の値は小文字。
    // 大文字小文字を吸収して比較する。
    const currentRole = String(user.role).toLowerCase();
    return menuItems.filter(item =>
      item.roles.some(r => String(r).toLowerCase() === currentRole)
    );
  }, [user?.role]);

  // メニュー項目をクリックしたときの処理をメモ化
  const handleMenuItemClick = useCallback((path: string) => {
    navigate(path);
    if (isMobile) {
      onDrawerToggle();
    }
  }, [navigate, isMobile, onDrawerToggle]);

  // ドロワーの内容をメモ化
  const drawer = (
    <Box>
      <Box sx={{ p: 2, textAlign: 'center' }}>
        {logoLoaded ? (
          <img
            src="/logo.png"
            alt={storeName}
            style={{ width: '80px', height: 'auto' }}
            onError={() => setLogoLoaded(false)}
          />
        ) : (
          <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
            {storeName}
          </Typography>
        )}
      </Box>
      <Divider />
      <List>
        {filteredMenuItems().map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleMenuItemClick(item.path)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.title} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={onLogout}>
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="ログアウト" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
          },
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </>
  );
});

export default Sidebar; 